import { memo } from "react";
import type { PropsWithChildren } from "react";

import { ScrollButton } from "@/components/Button/ScrollButton";
import { useScrollableNavigation } from "@/hooks/useScrollableNavigation";
import { cn } from "@/lib/cn";

type Props = PropsWithChildren<{
  /** ルート要素（relative）へ付与するクラス */
  rootClassName?: string;
  /** スクロールviewportへ付与するクラス */
  viewportClassName?: string;
  /** 横並びのコンテンツ要素へ付与するクラス */
  contentClassName?: string;
}>;

/**
 * ScrollableRow
 *
 * 横並びの行コンテンツを「1行固定 + シェブロンで横スクロール」にする汎用プリミティブ。
 *
 * - オーバーフロー時のみ左右シェブロンを表示
 * - クリックで表示領域幅ぶんスクロール
 * - disabled 時はシェブロンを非表示（ScrollButtonの仕様）
 */
export const ScrollableRow = memo(function ScrollableRow({
  rootClassName,
  viewportClassName,
  contentClassName,
  children,
}: Props) {
  const {
    containerRef,
    contentRef,
    handleScroll,
    scrollToPrevious,
    scrollToNext,
    isAtStart,
    isAtEnd,
  } = useScrollableNavigation();

  return (
    <div className={cn("flex relative min-w-0", rootClassName)}>
      <div
        ref={containerRef}
        className={cn("flex overflow-hidden w-full", viewportClassName)}
        onScroll={handleScroll}
      >
        <div
          ref={contentRef}
          className={cn("flex min-w-max", contentClassName)}
        >
          {children}
        </div>
      </div>

      <ScrollButton
        direction="left"
        onClick={scrollToPrevious}
        disabled={isAtStart}
        variant="bottomFill"
      />
      <ScrollButton
        direction="right"
        onClick={scrollToNext}
        disabled={isAtEnd}
        variant="bottomFill"
      />
    </div>
  );
});
